import React, { useState } from 'react';
import {
  Col,
  Form,
  FormItemProps,
  Input,
  InputProps,
  Row,
  Typography,
  Tooltip,
} from 'antd';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { REGEX } from 'src/constants/app';
import classNames from 'classnames';

type Props<T extends FieldValues> = {
  inputProps?: InputProps;
  controller: UseControllerProps<T>;
  errorCol?: 12 | 24;
  isOnlyInteger?: boolean;
  onBlur?: () => void;
  newRegex?: RegExp;
  showErrorTooltip?: boolean;
} & Omit<FormItemProps, 'children'>;

function NumberOnlyField<T extends FieldValues>(props: Props<T>) {
  const {
    inputProps,
    controller,
    errorCol = 24,
    label,
    isOnlyInteger,
    onBlur,
    newRegex,
    showErrorTooltip = false,
    ...rest
  } = props;
  const { fieldState, field } = useController<T>(controller);
  const { error } = fieldState;
  const [hovered, setHovered] = useState(false);

  const regex = isOnlyInteger
    ? REGEX.REGEX_ONLY_INTEGER_NUMBER
    : REGEX.REGEX_ONLY_NUMBER;

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setHovered(!showTooltip);
    // fix maxLength not work with japanese half-width characters
    if (inputProps?.maxLength && value.length > inputProps?.maxLength) {
      return;
    }

    const checkRegex = () => {
      return newRegex
        ? regex.test(value) && newRegex.test(value)
        : regex.test(value);
    };

    if ((!isNaN(Number(value)) && checkRegex()) || value === '') {
      field.onChange(value);
    } else {
      !field.value && field.onChange('');
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!regex.test(e.clipboardData.getData('Text'))) {
      e.preventDefault();
    }
  };

  const {
    className: inputClassName,
    style: inputStyle,
    ...restInputProps
  } = inputProps || {};

  const showTooltip = showErrorTooltip && !!error;
  const inputNode = (
    <Input
      {...field}
      {...restInputProps}
      onChange={onChangeInput}
      onPaste={onPaste}
      onBlur={() => onBlur && onBlur()}
      style={{ width: '100%', ...inputStyle }}
      className={classNames(inputClassName, {
        'form-tooltip-error': showTooltip,
      })}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    />
  );

  const fieldContent = (
    <Tooltip
      placement="topLeft"
      title={error?.message}
      overlayClassName="form-tooltip-error-tooltip"
      visible={hovered && showTooltip}
    >
      <span className="form-tooltip-wrapper">{inputNode}</span>
    </Tooltip>
  );

  return (
    <Form.Item
      colon={false}
      label={
        <Typography.Text
          style={{
            width: '100%',
          }}
        >
          {label}
        </Typography.Text>
      }
      labelAlign="left"
      labelCol={label ? { span: 24 } : undefined}
      wrapperCol={label ? { span: 24 } : undefined}
      validateStatus={error ? 'error' : ''}
      {...rest}
    >
      <Row gutter={4}>
        <Col span={errorCol}>
          <Row gutter={8}>
            <Col span={24}>{fieldContent}</Col>
          </Row>
        </Col>
        {!showTooltip && (
          <Col span={errorCol}>
            {error && (
              <Typography.Text
                className="ant-form-item-explain ant-form-item-explain-error"
                type="danger"
                ellipsis={{ tooltip: true }}
                style={{ lineHeight: errorCol === 24 ? '16px' : '42px' }}
              >
                {error.message}
              </Typography.Text>
            )}
          </Col>
        )}
      </Row>
    </Form.Item>
  );
}

export default NumberOnlyField;
